/**
 * Talking to the server about a PayPal purchase (buyers outside India; India pays through Razorpay).
 * The PayPal links never reach this code until the server hands one over for an order the signed-in buyer just
 * started: the app only ever shows buttons.
 */
import api from "@/lib/api";

/** Start an order. kind: "pack" | "pass"; key: pack_500 / practice_month ... Returns {order_ref, mode, url, title, amount, email}. */
export async function createPaypalOrder(kind, key) {
  const { data } = await api.post("/payments/paypal/create", { kind, key }, { timeout: 30000 });
  if (!data?.order_ref || !data?.url) throw new Error("Could not start the payment");
  return data;
}

/**
 * Has it been paid? The server looks, and credits only if PayPal says so; calling it again is always safe.
 * txnId: the buyer's PayPal transaction id, for a payment made with a different PayPal e-mail than their account's.
 * Returns {state: "paid"|"pending"|"problem"|"failed", ...}.
 */
export async function checkPaypalOrder(orderRef, txnId) {
  const body = { order_ref: orderRef };
  if (txnId) body.txn_id = txnId;
  const { data } = await api.post("/payments/paypal/check", body, { timeout: 45000 });
  return data;
}

/** Tell the server what happened on the buyer's screen (a blocked window, a failed start, giving up). Never throws. */
export function reportPaypalEvent(event, { orderRef, itemKey, reason } = {}) {
  try {
    api.post("/payments/paypal/event", { event, order_ref: orderRef || null, item_key: itemKey || null, reason: reason ? String(reason).slice(0, 190) : null },
      { timeout: 8000 }).catch(() => {});
  } catch { /* the trail is nice to have, never in the buyer's way */ }
}

/** Why a request failed, in words a buyer can act on. */
export function paypalErrorMessage(e, fallback = "Something went wrong. Please try again.") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  if (!e?.response) return "We couldn't reach the server. Check your connection and try again.";
  return fallback;
}
